import { useId, useState, useMemo, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { format } from 'date-fns';
import { adminService, UserActivityLogEntry } from '../services/admin.service';
import { userService, User } from '../services/user.service';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';

const USER_SUGGESTIONS_MAX = 10;

function getDisplayLabel(u: User): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name ? `${u.email} (${name})` : u.email;
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const FEATURE_OPTIONS = [
  { value: '', labelKey: 'common.all' },
  { value: 'samples', labelKey: 'user_activity.feature_samples' },
  { value: 'reservation', labelKey: 'user_activity.feature_reservation' },
  { value: 'gene-analysis', labelKey: 'user_activity.feature_gene_analysis' },
  { value: 'keisuke', labelKey: 'user_activity.feature_keisuke' },
  { value: 'billing', labelKey: 'user_activity.feature_billing' },
  { value: 'dashboard', labelKey: 'user_activity.feature_dashboard' },
  { value: 'reports', labelKey: 'user_activity.feature_reports' },
  { value: 'auth', labelKey: 'user_activity.feature_auth' },
  { value: 'admin', labelKey: 'user_activity.feature_admin' },
  { value: 'other', labelKey: 'user_activity.feature_other' },
];

const LIMIT_OPTIONS = [20, 50, 100] as const;

export default function UserActivityLogs() {
  const { t, i18n } = useTranslation();
  const embedded = useLocation().pathname.startsWith('/admin/insights/');
  const fieldId = useId();
  const [activeTab, setActiveTab] = useState<'list' | 'chart'>('list');
  const [userId, setUserId] = useState<string>('');
  const [userSearchText, setUserSearchText] = useState<string>('');
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [featureKey, setFeatureKey] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<typeof LIMIT_OPTIONS[number]>(LIMIT_OPTIONS[0]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const parsedUserId = userId ? parseInt(userId, 10) : undefined;
  const normalizedFeatureKey = FEATURE_OPTIONS.some((o) => o.value === featureKey) ? featureKey : '';
  const listParams = useMemo(
    () => ({
      page: Math.max(1, page),
      limit: LIMIT_OPTIONS.includes(limit) ? limit : LIMIT_OPTIONS[0],
      userId: parsedUserId !== undefined && Number.isInteger(parsedUserId) ? parsedUserId : undefined,
      featureKey: normalizedFeatureKey || undefined,
      search: searchKeyword.trim() || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    [page, limit, parsedUserId, normalizedFeatureKey, searchKeyword, startDate, endDate]
  );
  const chartParams = useMemo(
    () => ({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    [startDate, endDate]
  );

  useEffect(() => {
    setExportError(null);
  }, [userId, featureKey, searchKeyword, startDate, endDate]);

  const { data: usersData } = useQuery({
    queryKey: ['users-list-for-activity'],
    queryFn: () => userService.list({ limit: 500 }),
  });
  const users = usersData?.data ?? [];

  const userSuggestions = useMemo(() => {
    if (!userSearchText.trim()) return [];
    const q = userSearchText.trim().toLowerCase();
    return users
      .filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          (u.firstName?.toLowerCase().includes(q)) ||
          (u.lastName?.toLowerCase().includes(q)) ||
          u.username.toLowerCase().includes(q) ||
          getDisplayLabel(u).toLowerCase().includes(q)
      )
      .slice(0, USER_SUGGESTIONS_MAX);
  }, [users, userSearchText]);

  const { data: activityData, isLoading: activityLoading, error: activityError } = useQuery({
    queryKey: ['admin-user-activity', listParams],
    queryFn: () => adminService.listUserActivity(listParams),
  });

  const { data: featureData, isLoading: chartLoading } = useQuery({
    queryKey: ['admin-feature-usage', chartParams],
    queryFn: () => adminService.getFeatureUsage(chartParams),
    enabled: activeTab === 'chart',
  });

  const logs = activityData?.data?.logs ?? [];
  const pagination = activityData?.data?.pagination;
  const featureUsage = featureData?.data ?? [];

  const chartData = {
    labels: featureUsage.map((r) => r.label),
    datasets: [
      {
        label: t('user_activity.chart_dataset_label', { defaultValue: '利用回数' }),
        data: featureUsage.map((r) => r.count),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  };

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const exportUserId = userId ? parseInt(userId, 10) : undefined;
      const blob = await adminService.exportUserActivity({
        userId: exportUserId !== undefined && Number.isInteger(exportUserId) ? exportUserId : undefined,
        featureKey: normalizedFeatureKey || undefined,
        search: searchKeyword.trim() || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-activity_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(t('user_activity.export_error', { defaultValue: 'エクスポートに失敗しました。' }));
    } finally {
      setExporting(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: t('user_activity.chart_title', { defaultValue: '機能別利用回数' }),
      },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  return (
    <Container className="py-4">
      {!embedded && (
        <>
          <h1 className="mb-4">{t('user_activity.title', { defaultValue: 'ユーザー行動・機能利用' })}</h1>
          <p className="text-muted mb-4">
            {t('user_activity.description', {
              defaultValue: '登録ユーザーがいつ・どの機能を利用したかを個人別に確認し、機能別の利用状況をグラフで確認できます。',
            })}
          </p>
        </>
      )}

      <CollapsibleHelpSection
        titleKey="user_activity.help_title"
        storageKey="user_activity_logs"
        descriptionKey="user_activity.help_content"
      />

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab((k as 'list' | 'chart') ?? 'list')} className="mb-4">
        <Tab eventKey="list" title={t('user_activity.tab_list', { defaultValue: 'ユーザー別行動ログ' })}>
          <Card className="mb-4">
            <Card.Header>{t('user_activity.filters', { defaultValue: 'フィルター' })}</Card.Header>
            <Card.Body>
              <Row className="g-2">
                <Col md={3}>
                  <Form.Label htmlFor={`${fieldId}-list-keyword`}>
                    {t('user_activity.filter_keyword', { defaultValue: 'フリーワード検索' })}
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-list-keyword`}
                    name="userActivityFilterKeyword"
                    type="text"
                    placeholder={t('user_activity.filter_keyword_placeholder', { defaultValue: 'パス・メソッド・機能・ユーザー名など' })}
                    value={searchKeyword}
                    onChange={(e) => {
                      setSearchKeyword(e.target.value);
                      setPage(1);
                    }}
                    autoComplete="off"
                    maxLength={200}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label htmlFor={`${fieldId}-list-user-search`}>
                    {t('user_activity.filter_user', { defaultValue: 'ユーザー' })}
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      id={`${fieldId}-list-user-search`}
                      name="userActivityFilterUserSearch"
                      type="text"
                      placeholder={t('user_activity.filter_user_placeholder', { defaultValue: 'メールまたは氏名で検索…' })}
                      value={userSearchText}
                      onChange={(e) => {
                        setUserSearchText(e.target.value);
                        setUserId('');
                        setPage(1);
                        setShowUserSuggestions(true);
                      }}
                      onFocus={() => userSearchText.trim() && setShowUserSuggestions(true)}
                      onBlur={() => {
                        setTimeout(() => setShowUserSuggestions(false), 200);
                      }}
                      autoComplete="off"
                      style={userId ? { paddingRight: '1.75rem' } : undefined}
                    />
                    {userId && (
                      <Button
                        variant="link"
                        size="sm"
                        className="position-absolute end-0 top-50 translate-middle-y text-muted text-decoration-none me-1"
                        style={{ padding: '0 4px', fontSize: '0.875rem' }}
                        onClick={() => {
                          setUserId('');
                          setUserSearchText('');
                          setPage(1);
                        }}
                        aria-label={t('common.clear', { defaultValue: 'クリア' })}
                      >
                        ×
                      </Button>
                    )}
                    {showUserSuggestions && userSuggestions.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100 shadow-sm mt-1"
                        style={{ zIndex: 1050, maxHeight: 240, overflowY: 'auto' }}
                      >
                        {userSuggestions.map((u) => (
                          <li
                            key={u.id}
                            className="list-group-item list-group-item-action py-2 small"
                            role="option"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setUserId(String(u.id));
                              setUserSearchText(getDisplayLabel(u));
                              setShowUserSuggestions(false);
                              setPage(1);
                            }}
                          >
                            {getDisplayLabel(u)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Col>
                <Col md={2}>
                  <Form.Label htmlFor={`${fieldId}-list-feature`}>
                    {t('user_activity.filter_feature', { defaultValue: '機能' })}
                  </Form.Label>
                  <Form.Select
                    id={`${fieldId}-list-feature`}
                    name="userActivityFilterFeature"
                    value={normalizedFeatureKey}
                    onChange={(e) => {
                      setFeatureKey(e.target.value);
                      setPage(1);
                    }}
                  >
                    {FEATURE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>
                        {t(opt.labelKey, { defaultValue: opt.value || 'すべて' })}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label htmlFor={`${fieldId}-list-start-date`}>
                    {t('user_activity.filter_start', { defaultValue: '開始日' })}
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-list-start-date`}
                    name="userActivityFilterStartDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                    lang={i18n.language}
                  />
                </Col>
                <Col md={2}>
                  <Form.Label htmlFor={`${fieldId}-list-end-date`}>
                    {t('user_activity.filter_end', { defaultValue: '終了日' })}
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-list-end-date`}
                    name="userActivityFilterEndDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                    lang={i18n.language}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={exporting}
                    onClick={handleExport}
                    aria-busy={exporting}
                  >
                    {exporting
                      ? t('user_activity.exporting', { defaultValue: 'エクスポート中…' })
                      : t('user_activity.export_csv', { defaultValue: 'CSVエクスポート' })}
                  </Button>
                  {exportError && (
                    <span className="text-danger small ms-2">{exportError}</span>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {activityError && (
            <Alert variant="danger">
              {t('user_activity.error_load', { defaultValue: 'データの読み込みに失敗しました。' })}
            </Alert>
          )}
          {activityLoading ? (
            <div className="text-center py-5" role="status" aria-label={t('common.loading', { defaultValue: '読み込み中' })}>
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {pagination && (
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2 border-bottom mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">
                      {pagination.total === 0
                        ? `0 ${t('user_activity.items', { defaultValue: '件' })}`
                        : `${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.total)} / ${pagination.total} ${t('user_activity.items', { defaultValue: '件' })}`}
                    </span>
                    <Form.Label className="visually-hidden" htmlFor={`${fieldId}-list-per-page`}>
                      {t('user_activity.per_page', { defaultValue: '件/ページ' })}
                    </Form.Label>
                    <Form.Select
                      id={`${fieldId}-list-per-page`}
                      name="userActivityListPerPage"
                      size="sm"
                      style={{ width: 'auto' }}
                      value={LIMIT_OPTIONS.includes(limit) ? limit : LIMIT_OPTIONS[0]}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setLimit((LIMIT_OPTIONS.includes(n as typeof LIMIT_OPTIONS[number]) ? n : LIMIT_OPTIONS[0]) as typeof LIMIT_OPTIONS[number]);
                        setPage(1);
                      }}
                      aria-label={t('user_activity.per_page', { defaultValue: '件/ページ' })}
                    >
                      {LIMIT_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                          {t('user_activity.per_page', { defaultValue: '件/ページ' })}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage(1)}
                      title={t('user_activity.page_first', { defaultValue: '最初のページ' })}
                    >
                      {t('user_activity.page_first_short', { defaultValue: '最初' })}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      {t('common.prev', { defaultValue: '前へ' })}
                    </Button>
                    <span className="px-2 small">
                      {t('user_activity.page_of', {
                        defaultValue: '{{current}} / {{total}}',
                        current: pagination.page,
                        total: Math.max(1, pagination.totalPages),
                      })}
                    </span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t('common.next', { defaultValue: '次へ' })}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => setPage(Math.max(1, pagination.totalPages))}
                      title={t('user_activity.page_last', { defaultValue: '最後のページ' })}
                    >
                      {t('user_activity.page_last_short', { defaultValue: '最後' })}
                    </Button>
                  </div>
                </div>
              )}
              <Table bordered hover size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th scope="col">{t('user_activity.th_time', { defaultValue: '日時' })}</th>
                    <th scope="col">{t('user_activity.th_user', { defaultValue: 'ユーザー' })}</th>
                    <th scope="col">{t('user_activity.th_feature', { defaultValue: '機能' })}</th>
                    <th scope="col">{t('user_activity.th_method', { defaultValue: 'メソッド' })}</th>
                    <th scope="col">{t('user_activity.th_path', { defaultValue: 'パス' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: UserActivityLogEntry) => (
                    <tr key={String(log.id)}>
                      <td>
                        {log.createdAt && !Number.isNaN(new Date(log.createdAt).getTime())
                          ? format(new Date(log.createdAt), 'yyyy/MM/dd HH:mm:ss')
                          : '-'}
                      </td>
                      <td>
                        {log.user?.email ?? '-'}
                        <br />
                        <small className="text-muted">
                          {log.user?.firstName} {log.user?.lastName} (ID: {log.userId})
                        </small>
                      </td>
                      <td>{log.featureKey}</td>
                      <td>{log.method}</td>
                      <td className="small text-break">{log.path}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {logs.length === 0 && (
                <p className="text-muted text-center py-4">
                  {t('user_activity.no_data', { defaultValue: '該当するログはありません。' })}
                </p>
              )}
            </>
          )}
        </Tab>
        <Tab eventKey="chart" title={t('user_activity.tab_chart', { defaultValue: '機能別利用状況' })}>
          <Card className="mb-4">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Label htmlFor={`${fieldId}-chart-start-date`}>
                    {t('user_activity.filter_start', { defaultValue: '開始日' })}
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-chart-start-date`}
                    name="userActivityChartStartDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    lang={i18n.language}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label htmlFor={`${fieldId}-chart-end-date`}>
                    {t('user_activity.filter_end', { defaultValue: '終了日' })}
                  </Form.Label>
                  <Form.Control
                    id={`${fieldId}-chart-end-date`}
                    name="userActivityChartEndDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    lang={i18n.language}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          {chartLoading ? (
            <div className="text-center py-5" role="status" aria-label={t('common.loading', { defaultValue: '読み込み中' })}>
              <Spinner animation="border" />
            </div>
          ) : featureUsage.length === 0 ? (
            <p className="text-muted text-center py-4">
              {t('user_activity.chart_no_data', { defaultValue: '指定期間のデータがありません。' })}
            </p>
          ) : (
            <div style={{ height: 400 }}>
              <Bar data={chartData} options={chartOptions} />
            </div>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
}
